import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { 
  LayoutDashboard, 
  MessageSquare, 
  CheckSquare, 
  Users, 
  FileCheck, 
  Settings,
  Activity,
  Plus,
  Search,
  User,
  Calendar
} from 'lucide-react';
import { useTasks } from '@/hooks/useTasks';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useApprovals } from '@/hooks/useApprovals';
import { Badge } from '@/components/ui/badge';

const navigationItems = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'AI Assistant', href: '/chat', icon: MessageSquare },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare },
  { name: 'Team', href: '/team', icon: Users },
  { name: 'Approvals', href: '/approvals', icon: FileCheck },
  { name: 'Activity Log', href: '/activity', icon: Activity },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const quickActions = [
  { name: 'Create New Task', action: 'new-task', icon: Plus },
  { name: 'Ask AI Assistant', action: 'chat', icon: MessageSquare },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { tasks } = useTasks();
  const { teamMembers } = useTeamMembers();
  const { approvals } = useApprovals();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleSelect = useCallback((value: string) => {
    setOpen(false);
    
    if (value.startsWith('/')) {
      navigate(value);
    } else if (value === 'new-task') {
      navigate('/tasks?action=new');
    } else if (value === 'chat') {
      navigate('/chat');
    } else if (value.startsWith('task:')) {
      navigate('/tasks');
    } else if (value.startsWith('team:')) {
      navigate('/team');
    } else if (value.startsWith('approval:')) {
      navigate('/approvals');
    }
  }, [navigate]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'outline';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'rejected': return 'destructive';
      case 'pending': return 'secondary';
      default: return 'outline';
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search tasks, team members, or navigate..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        
        <CommandGroup heading="Quick Actions">
          {quickActions.map((item) => (
            <CommandItem
              key={item.action}
              value={item.action}
              onSelect={handleSelect}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.name}
            </CommandItem>
          ))}
        </CommandGroup>
        
        <CommandSeparator />
        
        <CommandGroup heading="Navigation">
          {navigationItems.map((item) => (
            <CommandItem
              key={item.href}
              value={item.href}
              onSelect={handleSelect}
            >
              <item.icon className="mr-2 h-4 w-4" />
              {item.name}
            </CommandItem>
          ))}
        </CommandGroup>
        
        {tasks.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Tasks">
              {tasks.slice(0, 5).map((task) => (
                <CommandItem
                  key={task.id}
                  value={`task:${task.id}:${task.title}`}
                  onSelect={handleSelect}
                >
                  <CheckSquare className="mr-2 h-4 w-4" />
                  <span className="flex-1 truncate">{task.title}</span>
                  <Badge variant={getPriorityColor(task.priority)} className="ml-2 text-xs">
                    {task.priority}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        
        {teamMembers.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Team Members">
              {teamMembers.slice(0, 5).map((member) => (
                <CommandItem
                  key={member.id}
                  value={`team:${member.id}:${member.name}:${member.email}`}
                  onSelect={handleSelect}
                >
                  <User className="mr-2 h-4 w-4" />
                  <span className="flex-1">{member.name}</span>
                  <span className="text-xs text-muted-foreground">{member.role}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
        
        {approvals.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Approvals">
              {approvals.slice(0, 5).map((approval) => (
                <CommandItem
                  key={approval.id}
                  value={`approval:${approval.id}:${approval.title}`}
                  onSelect={handleSelect}
                >
                  <FileCheck className="mr-2 h-4 w-4" />
                  <span className="flex-1 truncate">{approval.title}</span>
                  <Badge variant={getStatusColor(approval.status)} className="ml-2 text-xs">
                    {approval.status}
                  </Badge>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
